import * as React from 'react';
import { createPortal } from 'react-dom';
import { XIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

export interface ToastProps {
  open: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  className?: string;
  /**
   * Position of the toast on screen
   * @default 'bottom'
   */
  position?: 'top' | 'bottom';
}

export interface ToastActionProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'outline';
}

const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  ({ open, onClose, children, className, position = 'bottom' }, ref) => {
    if (!open) return null;

    // Use portal to render at document.body level, avoiding overflow clipping
    // on mobile Safari/Chrome where overflow:auto on ancestors breaks position:fixed
    return createPortal(
      <div
        ref={ref}
        role="alert"
        aria-live="polite"
        className={cn(
          'fixed left-4 right-4 z-[9999] pointer-events-auto mx-auto max-w-md',
          'rounded-lg bg-[var(--slate-600)] p-4 shadow-lg',
          'animate-in fade-in-0 slide-in-from-bottom-5',
          position === 'bottom' ? 'bottom-28' : 'top-4',
          className
        )}
      >
        <div className="flex items-start gap-3">
          <div className="flex-1">{children}</div>
          {onClose && (
            <button
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onClose();
              }}
              className="shrink-0 rounded p-1 text-[var(--slate-400)] transition-colors hover:bg-[var(--slate-500)] hover:text-[var(--slate-50)]"
              aria-label="Dismiss"
            >
              <XIcon size={18} weight="bold" />
            </button>
          )}
        </div>
      </div>,
      document.body
    );
  }
);
Toast.displayName = 'Toast';

const ToastTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h4
    ref={ref}
    className={cn('text-large text-[var(--slate-50)]', className)}
    {...props}
  />
));
ToastTitle.displayName = 'ToastTitle';

const ToastDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('mt-1 text-body text-[var(--slate-300)]', className)}
    {...props}
  />
));
ToastDescription.displayName = 'ToastDescription';

const ToastActions = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('mt-3 flex items-center gap-2', className)}
    {...props}
  />
));
ToastActions.displayName = 'ToastActions';

const ToastAction = React.forwardRef<HTMLButtonElement, ToastActionProps>(
  ({ className, variant = 'default', ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center rounded-md py-1.5 text-body transition-colors h-12 px-6',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--purple-400)] focus-visible:ring-offset-2',
          'disabled:pointer-events-none disabled:opacity-50',
          variant === 'default' &&
            'bg-[var(--green-400)] text-[var(--slate-100)] hover:bg-[var(--green-300)]',
          variant === 'outline' &&
            'border border-[var(--slate-400)] text-[var(--slate-200)] hover:bg-[var(--slate-500)]',
          className
        )}
        {...props}
      />
    );
  }
);
ToastAction.displayName = 'ToastAction';

export { Toast, ToastTitle, ToastDescription, ToastActions, ToastAction };
